import { useState, useEffect, useRef, useCallback, Fragment, useContext } from 'react';
import '../styles/proposalPrint.css';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';
import { ToastContainer } from 'react-toastify';

import { CssBaseline, Box, Typography, Paper, Radio, RadioGroup, FormControlLabel, Button as MuiButton, Grid, Tooltip, Chip, Dialog, DialogTitle, DialogContent, Divider, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { AuthContext } from '../context/AuthProvider';

// Custom Components
import {
  Spinner,
  BrowserTitle,
  EmailProposalModal,
  ProposalViewHeader,
  ProposalViewEditSection,
  ProposalViewAwards,
  ProposalViewContacts,
  ProposalViewSystemEquipment,
  ProposalViewSystemWorks,
  ProposalViewSystemWarranties,
  ProposalViewSystemRebatesAndInstants,
  ProposalViewAdditionalInfo,
  ProposalViewJobOverview,
  ProposalViewEquipmentOverview,
  ProposalViewAirflow,
  ProposalViewWorkDescription,
  ProposalViewInvestment,
  ProposalViewGuarantees,
  ProposalViewFooter,
  ProposalViewSignature,
  EquipmentImage,
} from '../components';

// Utils
import messages from '../static/messages';
import { calculateTotalPay, calculateTotalPayForOption, calculateTotalRebates, calculateTotalInstants, calculateEquipmentTotalForOption } from '../utils/proposalCalculations';

// Services
import proposalsServices from '../services/proposalsServices';
import proposalOptionServices from '../services/proposalOptionServices';
import publicServices from '../services/publicServices';
import equipmentTypesServices from '../services/equipmentTypesServices';
import { prepareHtml } from '../utils/htmlUtils';

// Font family constant
const fontFamily = '"Inter", sans-serif';

// Styles
const no_print = {
  '@media print': {
    display: 'none',
  },
};

const print_styles = {
  '@media print': {
    '& p': {
      breakInside: 'avoid',
    },
  },
};

export const container = {
  maxWidth: 950,
  margin: '0 auto',
};

// External variables

const ProposalView1 = () => {
  const [proposal, setProposal] = useState();
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [sign, setSign] = useState();
  const [token, setToken] = useState();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [equipmentTypes, setEquipmentTypes] = useState({});
  const [smallLogo, setSmallLogo] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [viewsOpen, setViewsOpen] = useState(false);
  const [viewsData, setViewsData] = useState(null);
  const [viewsLoading, setViewsLoading] = useState(false);

  const componentRef = useRef(null);

  const queryParams = new URLSearchParams(window.location.search);
  const userToken = queryParams.get('token');

  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  
  // Get auth context to check if user is logged in
  const { userData, isAuthenticated, token: authToken } = useContext(AuthContext);
  // User can sign if authenticated (has JWT token) and not viewing via client token URL
  const isAuthUser = isAuthenticated && !!authToken && !userToken;

  // Action functions
  const onAcceptProposal = (signData) => {
    setSign(signData);
  };

  const onPrintContent = useCallback(() => {
    return componentRef.current;
  }, [componentRef.current]);

  // Handler functions
  const handleEmailModalOpen = () => setEmailModalOpen(!emailModalOpen);
  const handleEmailModalClose = () => setEmailModalOpen(false);
  const handleSignatureModalOpen = () => {
    // Scroll to signature section
    const signatureEl = document.getElementById('signature-section');
    if (signatureEl) {
      signatureEl.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePrint = useReactToPrint({
    content: onPrintContent,
    documentTitle: `proposal_${proposal?.publicId || id}`,
    removeAfterPrint: true,
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 0;
      }
      @media print {
        html, body {
          height: auto !important;
          overflow: visible !important;
          background: white !important;
          margin: 0 !important;
          padding: 0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body * {
          visibility: visible !important;
        }
        .proposal-print-paper {
          display: block !important;
          visibility: visible !important;
          position: static !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 12mm 10mm !important;
          box-shadow: none !important;
        }
      }
    `,
  });

  const handlePdf = async () => {
    if (!componentRef.current) return;
    
    setPdfLoading(true);
    const element = componentRef.current;
    const originalStyle = element.style.cssText;
    // Hide no-print elements (edit buttons, etc.) — html2canvas ignores @media print
    const noPrintEls = element.querySelectorAll('.no-print');
    noPrintEls.forEach(el => el.style.display = 'none');
    
    try {
      
      // Set fixed width for consistent rendering
      element.style.width = '794px'; // A4 width at 96dpi
      element.style.maxWidth = '794px';
      element.style.margin = '0';
      element.style.padding = '20px';
      
      // Wait for reflow
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Proposal-${proposal?.publicId || id}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          letterRendering: true,
          windowWidth: 794,
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        pagebreak: { 
          mode: ['avoid-all', 'css', 'legacy'],
          avoid: [
            '.pdf-section',
            '.print-section',
            '.equipment-card',
            '.equipment-system-group',
            '.airflow-item',
            '.signature-section',
            'tr',
            'img'
          ]
        }
      };
      
      await html2pdf().set(opt).from(element).save();
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      // Restore original styles and no-print elements (even on error)
      element.style.cssText = originalStyle;
      noPrintEls.forEach(el => el.style.display = '');
      setPdfLoading(false);
    }
  };


  // Format a view timestamp in the company timezone for the staff history UI.
  const formatViewDate = (value) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch (e) {
      return new Date(value).toLocaleString('en-US');
    }
  };

  const handleOpenViews = async () => {
    setViewsOpen(true);
    setViewsLoading(true);
    try {
      const response = await proposalsServices.getViews(id);
      setViewsData(response.data);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    } finally {
      setViewsLoading(false);
    }
  };

  // Async functions
  const getProposal = async () => {
    try {
      const response = await proposalsServices.getProposalById(id);
      if (response.status === 200) {
        setProposal(response.data);
      }
    } catch (error) {
      if (error?.response?.data.status === 404) {
        navigate('/404');
      } else {
        toast.error(messages.errors.error_data_loaning);
        console.log(error);
      }
    }
  };

  const getPublicProposal = async (userToken) => {
    try {
      const response = await publicServices.getPublicProposal(id, userToken);
      setProposal(response.data);
    } catch (error) {
      if (error.response.data.status === 404) {
        navigate('/404');
      } else if (error.response.data.status === 401) {
        navigate('/401');
      } else {
        toast.error(messages.errors.error_data_loaning);
        console.log(error);
      }
    }
  };

  const updatePublicProposal = async () => {
    try {
      const response = await publicServices.updatePublicProposal(id, token, {
        data: {
          ...proposal.data,
        },
        signature: {
          sign: sign,
        },
      });

      if (response.status === 200) {
        toast.success('Proposal accepted successfully!');
        getPublicProposal(userToken);
      }
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  // Sign proposal as authenticated user (technician/admin)
  const signProposalAsUser = async () => {
    try {
      const response = await proposalsServices.signProposal(id, sign);

      if (response.status === 200) {
        toast.success('Proposal signed successfully!');
        getProposal(); // Reload proposal to show signature
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to sign proposal');
      console.log(error);
    }
  };

  // Load equipment types with their default images
  const loadEquipmentTypes = async () => {
    try {
      const response = await equipmentTypesServices.getAll();
      if (response.status === 200) {
        const typesMap = {};
        response.data.forEach(type => {
          typesMap[type.name] = type.imageUrl || null;
        });
        setEquipmentTypes(typesMap);
      }
    } catch (error) {
      console.log('Error loading equipment types:', error);
    }
  };

  const handleAcceptOption = async (optionId) => {
    try {
      let response;
      if (token) {
        response = await publicServices.acceptOptionByClientToken(id, optionId, token);
      } else {
        response = await proposalOptionServices.acceptOption(id, optionId);
      }
      if (response.status === 200) {
        toast.success('Option accepted!');
        if (token) {
          getPublicProposal(userToken);
        } else {
          getProposal();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to accept option');
      console.log(error);
    }
  };

  // Hooks
  useEffect(() => {
    if (userToken) {
      setToken(userToken);
      getPublicProposal(userToken);
    } else {
      getProposal();
      loadEquipmentTypes();
    }
  }, []);

  useEffect(() => {
    if (sign) {
      if (isAuthUser) {
        // User is authenticated - use internal API
        signProposalAsUser();
      } else if (token) {
        // Client with token - use public API
        updatePublicProposal();
      }
    }
  }, [sign]);

  // Determine if proposal has active (non-accepted) options
  const proposalOptions = proposal?.proposalOptions || [];
  const hasActiveOptions = proposalOptions.length > 0 && !proposal?.optionAccepted;

  const effectiveSelectedOptionId = selectedOptionId
    || (hasActiveOptions && proposalOptions.length > 0 ? proposalOptions[0].id : null);

  // Initialize selectedOptionId from first option if not set
  useEffect(() => {
    if (hasActiveOptions && !selectedOptionId && proposalOptions.length > 0) {
      setSelectedOptionId(proposalOptions[0].id);
    }
  }, [hasActiveOptions, selectedOptionId, proposalOptions]);

  // Check for data loading
  if (!proposal) return <Spinner />;

  // Calculate totals — use option-specific calc when an option is selected or accepted
  const activeOptionId = hasActiveOptions
    ? effectiveSelectedOptionId
    : (proposal.optionAccepted ? proposal.selectedOptionId : null);
  const totalPay = activeOptionId
    ? calculateTotalPayForOption(proposal, activeOptionId)
    : calculateTotalPay(proposal);
  const totalRebates = calculateTotalRebates(proposal);
  const totalInstants = calculateTotalInstants(proposal);

  // Get job overview text
  const getJobOverview = () => {
    // First check direct jobOverview field
    if (proposal.jobOverview) {
      return proposal.jobOverview;
    }
    // Fallback: Check new structure (proposalSystems.notes)
    if (proposal.proposalSystems) {
      const overviewParts = [];
      proposal.proposalSystems.forEach(system => {
        if (system.notes) {
          overviewParts.push(system.notes);
        }
      });
      if (overviewParts.length > 0) return overviewParts.join('\n\n');
    }
    return null;
  };

  // Build line items for investment section
  const getLineItems = () => {
    // Line items (Equipment, Installation, etc.) are internal info for salesman
    // Client only sees final price and discounts/rebates
    return [];
  };

  return (
    <Fragment>
      <CssBaseline />
      <BrowserTitle title="Airmaxx Pro | Proposal View" />

      <ProposalViewHeader
        handlePrint={handlePrint}
        handlePdf={handlePdf}
        pdfLoading={pdfLoading}
        handleEmailModalOpen={handleEmailModalOpen}
        handleSignatureModalOpen={handleSignatureModalOpen}
        isAccepted={!!proposal.signature}
        token={!!token}
        totalPay={totalPay}
        proposal={proposal}
        onRefresh={token ? getPublicProposal : getProposal}
      />

      <Box 
        component="main" className="proposal-print-container" 
        sx={{ 
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)', 
          pt: { xs: '70px', sm: '80px' },
          pb: { xs: 2, sm: 5 },
          fontFamily,
          minHeight: '100vh',
          '@media print': {
            background: 'white !important',
            padding: '0 !important',
            minHeight: 'auto !important',
          },
          ...print_styles 
        }}
      >
        <ProposalViewEditSection id={id} client={!!token} smallLogo={smallLogo} setSmallLogo={setSmallLogo} />

        {/* Staff-only: did the client open the link? (excluded from print/PDF) */}
        {!token && (
          <Box
            sx={{
              maxWidth: 850,
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 1,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: proposal.viewCount > 0 ? '#e8f5e9' : '#f5f5f5',
              border: `1px solid ${proposal.viewCount > 0 ? '#a5d6a7' : '#e0e0e0'}`,
              ...no_print,
            }}
          >
            <VisibilityIcon sx={{ color: proposal.viewCount > 0 ? '#2e7d32' : '#9e9e9e' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {proposal.viewCount > 0
                ? `Client opened the link on ${proposal.viewCount} ${proposal.viewCount === 1 ? 'day' : 'days'}`
                : 'Client has not opened the link yet'}
            </Typography>
            {proposal.lastViewedAt && (
              <Typography variant="caption" color="text.secondary">
                Last opened: {formatViewDate(proposal.lastViewedAt)}
              </Typography>
            )}
            {proposal.viewCount > 0 && (
              <MuiButton size="small" variant="text" onClick={handleOpenViews} sx={{ ml: 'auto' }}>
                View history
              </MuiButton>
            )}
          </Box>
        )}

        <Paper 
          sx={{ 
            maxWidth: 850, 
            margin: '1rem auto 0', 
            padding: '0', 
            fontFamily,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: 2,
            overflow: 'hidden',
            '@media print': {
              maxWidth: '100% !important',
              width: '100% !important',
              margin: '0 !important',
              boxShadow: 'none !important',
              borderRadius: '0 !important',
            },
          }} 
          ref={componentRef} className="proposal-print-paper"
        >
          {/* PAGE 1: Header and Overview */}
          <Box sx={{ p: 3 }}>
            {/* Header with Contract ID, Comfort Advisor, Cell, Issue Date, Install Date */}
            <ProposalViewAwards 
              techName={proposal.createdBy?.fullName}
              techPhone={proposal.createdBy?.phoneNumber}
              techEmail={proposal.createdBy?.email}
              proposalId={proposal.publicId}
              issueDate={proposal.createdAt}
              installationSchedule={proposal.installationSchedule}
              smallLogo={smallLogo}
            />

            {/* Customer Info */}
            <ProposalViewContacts
              property={proposal.property}
              selectedClientId={proposal.selectedClientId}
              billingAddress={proposal.billingAddress}
            />
            
            {/* Installation Proposal: Job Overview */}
            {proposal.proposalType !== 'Service' && (
              <ProposalViewJobOverview description={getJobOverview()} />
            )}
            
            {/* Service Proposal: Equipment Overview */}
            {proposal.proposalType === 'Service' && (
              <ProposalViewEquipmentOverview
                description={proposal.equipmentOverview}
                proposalId={id}
                onRefresh={token ? () => getPublicProposal(token) : getProposal}
                editable={!token}
              />
            )}

            {/* Document Type Title */}
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <Typography 
                sx={{ 
                  fontFamily,
                  fontWeight: 700,
                  fontSize: 20,
                  color: '#0d3c61',
                  pb: 0.75,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  borderBottom: '2px solid #0d3c61',
                }}
              >
                {proposal.proposalType === 'Service' ? 'Proposal' : 'Home Improvement Contract'}
              </Typography>
            </Box>

            {/* SERVICE PROPOSAL: Work Description */}
            {proposal.proposalType === 'Service' && proposal.workDescriptions && proposal.workDescriptions.length > 0 && (
              <ProposalViewWorkDescription
                items={proposal.workDescriptions}
                proposalId={id}
                onRefresh={token ? () => getPublicProposal(token) : getProposal}
                editable={!token}
              />
            )}

            {/* INSTALLATION PROPOSAL: Equipment, Scope of Work, Warranties */}
            {/* New structure (proposalSystems) - Installation only */}
            {proposal.proposalType !== 'Service' && proposal.proposalSystems && proposal.proposalSystems.length > 0 && (
              <>
                {/* Equipment Block */}
                {(() => {
                  // If options exist and not yet accepted — show option columns
                  if (hasActiveOptions && proposalOptions.length > 0) {
                    return (
                      <Box className="print-section pdf-section" sx={{ mb: 4, px: 2 }}>
                        {/* Equipment Header */}
                        <Box className="equipment-header" sx={{ display: 'inline-block', mb: 2 }}>
                          <Box sx={{ backgroundColor: '#0d3c61', color: '#fff', px: 2, py: 0.75 }}>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Equipment
                            </Typography>
                          </Box>
                        </Box>

                        {/* Option Columns */}
                        <Grid container spacing={2}>
                          {[...proposalOptions]
                            .sort((a, b) => a.orderIndex - b.orderIndex)
                            .map((option) => {
                              const optionSystems = (proposal.proposalSystems || [])
                                .filter(s => s.optionId === option.id && s.equipments && s.equipments.length > 0);
                              const isSelected = effectiveSelectedOptionId === option.id;

                              return (
                                <Grid item xs={12} sm={12 / proposalOptions.length} key={option.id}>
                                  <Box sx={{
                                    border: isSelected ? '2px solid #0d3c61' : '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    p: 2,
                                    backgroundColor: isSelected ? '#f0f7ff' : '#fafafa',
                                    transition: 'all 0.2s ease',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}>
                                    {/* Radio + Option Name */}
                                    <Box
                                      sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }}
                                      onClick={() => setSelectedOptionId(option.id)}
                                    >
                                      <Radio
                                        checked={isSelected}
                                        onChange={() => setSelectedOptionId(option.id)}
                                        sx={{ p: 0.5, mr: 1 }}
                                      />
                                      <Typography sx={{
                                        fontFamily, fontWeight: 700, fontSize: 13,
                                        textTransform: 'uppercase', letterSpacing: '0.5px', color: '#0d3c61',
                                      }}>
                                        {option.name}
                                      </Typography>
                                    </Box>

                                    {/* Model list */}
                                    <Box sx={{ flex: 1 }}>
                                      {optionSystems.map((system) => (
                                        <Box key={system.id} sx={{ mb: 1.5 }}>
                                          {proposalOptions.length > 1 && optionSystems.length > 1 && (
                                            <Typography sx={{ fontFamily, fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', mb: 0.5 }}>
                                              {system.name}
                                            </Typography>
                                          )}
                                          {[...(system.equipments || [])].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)).map((eq) => (
                                            <Typography key={eq.id} sx={{ fontFamily, fontSize: 13, color: '#333', py: 0.3, borderBottom: '1px solid #f0f0f0' }}>
                                              {eq.manufacturerName && <span style={{ fontWeight: 700 }}>{eq.manufacturerName}</span>}{eq.manufacturerName ? ' ' : ''}{eq.modelNumber}
                                            </Typography>
                                          ))}
                                        </Box>
                                      ))}
                                      {optionSystems.length === 0 && (
                                        <Typography sx={{ fontFamily, fontSize: 12, color: '#999', fontStyle: 'italic' }}>
                                          No equipment
                                        </Typography>
                                      )}
                                    </Box>

                                    {/* Accept Button */}
                                    <MuiButton
                                      variant={isSelected ? "contained" : "outlined"}
                                      fullWidth
                                      onClick={() => handleAcceptOption(option.id)}
                                      sx={{
                                        mt: 2,
                                        fontFamily,
                                        fontWeight: 600,
                                        fontSize: 12,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        backgroundColor: isSelected ? '#0d3c61' : 'transparent',
                                        borderColor: '#0d3c61',
                                        color: isSelected ? '#fff' : '#0d3c61',
                                        '&:hover': {
                                          backgroundColor: isSelected ? '#0a2d4a' : 'rgba(13,60,97,0.08)',
                                        },
                                      }}
                                    >
                                      Accept This Option
                                    </MuiButton>

                                    {/* Contract price for this option */}
                                    <Typography
                                      sx={{
                                        fontFamily,
                                        fontWeight: 700,
                                        fontSize: 15,
                                        color: '#4caf50',
                                        textAlign: 'center',
                                        mt: 1.5,
                                      }}
                                    >
                                      {calculateTotalPayForOption(proposal, option.id).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                    </Typography>
                                  </Box>
                                </Grid>
                              );
                            })}
                        </Grid>
                      </Box>
                    );
                  }

                  // Standard equipment view (no options, or option accepted)
                  let displaySystems = proposal.proposalSystems;
                  if (proposal.optionAccepted && proposal.selectedOptionId) {
                    displaySystems = proposal.proposalSystems.filter(s => s.optionId === proposal.selectedOptionId);
                  }

                  const systemsWithEquipment = displaySystems
                    .filter(system => system.equipments && system.equipments.length > 0)
                    .map(system => ({
                      ...system,
                      equipments: [...system.equipments].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
                    }));
                  
                  if (systemsWithEquipment.length === 0) return null;
                  
                  const showSystemNames = displaySystems.length > 1;
                  
                  return (
                    <Box className="print-section pdf-section" sx={{ mb: 4, px: 2 }}>
                      <Box className="equipment-header" sx={{ display: 'inline-block', mb: 0 }}>
                        <Box sx={{ backgroundColor: '#0d3c61', color: '#fff', px: 2, py: 0.75 }}>
                          <Typography sx={{ fontFamily, fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Equipment
                          </Typography>
                        </Box>
                      </Box>
                      
                      {systemsWithEquipment.map((system, sysIdx) => (
                        <Box key={system.id || sysIdx} className="equipment-system-group" sx={{ mb: sysIdx < systemsWithEquipment.length - 1 ? 3 : 0, "@media print": { breakInside: "avoid", pageBreakInside: "avoid" } }}>
                          {showSystemNames && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 600, color: '#0d3c61', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e0e0', pb: 0.5, display: 'inline-block' }}>
                                {system.name}
                              </Typography>
                            </Box>
                          )}
                          
                          <Box className="equipment-grid" sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', print: 'repeat(3, 1fr)' },
                            gap: { xs: 1, sm: 2, print: 1 },
                          }}>
                            {system.equipments.map((equipment, idx) => {
                              const displayImageUrl = equipment.imageUrl || equipmentTypes[equipment.equipmentType] || null;
                              return (
                                <Box 
                                  key={equipment.id || idx}
                                  className="equipment-card"
                                  sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, p: { xs: 1.5, sm: 2, print: 1 }, display: 'flex', flexDirection: 'column', "@media print": { breakInside: "avoid", pageBreakInside: "avoid" } }}
                                >
                                  {displayImageUrl && (
                                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                                      <EquipmentImage src={displayImageUrl} alt={equipment.equipmentName || equipment.modelNumber} />
                                    </Box>
                                  )}
                                  <Typography sx={{ fontFamily, fontSize: 14, fontWeight: 500, color: '#1a1a1a', mb: 0.5 }}>
                                    {equipment.equipmentName || equipment.modelNumber}
                                  </Typography>
                                  {equipment.description && (
                                    <Typography sx={{ fontFamily, fontSize: 12, color: '#666', lineHeight: 1.5 }}>
                                      {equipment.description}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </>
            )}

            {/* Airflow Block - Installation only */}
            {proposal.proposalType !== 'Service' && proposal.proposalAirflows && proposal.proposalAirflows.length > 0 && (
              <ProposalViewAirflow airflows={proposal.proposalAirflows} />
            )}

            {/* Scope of Work Block - Installation only */}
                {proposal.proposalType !== 'Service' && (() => {
                  let allScopeOfWorks = [];
                  
                  // New structure: proposal.scopeOfWorks (contract level)
                  if (proposal.scopeOfWorks && proposal.scopeOfWorks.length > 0) {
                    allScopeOfWorks = proposal.scopeOfWorks.map(w => ({ ...w }));
                  } 
                  // Fallback: aggregate from proposalSystems (filtered by accepted option if applicable)
                  else if (proposal.proposalSystems) {
                    const filteredSystems = proposal.optionAccepted && proposal.selectedOptionId
                      ? proposal.proposalSystems.filter(s => s.optionId === proposal.selectedOptionId)
                      : proposal.proposalSystems;
                    filteredSystems.forEach((system) => {
                      if (system.scopeOfWorks && system.scopeOfWorks.length > 0) {
                        system.scopeOfWorks.forEach((w) => {
                          allScopeOfWorks.push({ ...w, systemName: system.name });
                        });
                      }
                    });
                  }
                  
                  if (allScopeOfWorks.length === 0) return null;
                  
                  return (
                    <Box className="print-section pdf-section" sx={{ mb: 4, px: 2 }}>
                      {/* Main Title */}
                      <Typography
                        sx={{
                          fontFamily,
                          fontWeight: 700,
                          fontSize: 20,
                          color: "#0d3c61",
                          textAlign: "center",
                          mb: 3,
                        }}
                      >
                        Scope of Work
                      </Typography>
                      
                      {/* Description Header */}
                      <Box sx={{ display: 'inline-block', mb: 0 }}>
                        <Box
                          sx={{
                            backgroundColor: '#0d3c61',
                            color: '#fff',
                            px: 2,
                            py: 0.75,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily,
                              fontWeight: 600,
                              fontSize: 14,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Description
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ backgroundColor: "#f8f9fa", borderRadius: 1, p: 2 }}>
                        {allScopeOfWorks.map((work, idx) => (
                          <Box 
                            key={idx} 
                            sx={{ 
                              display: "flex", 
                              alignItems: "flex-start",
                              gap: 1.5,
                              py: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                backgroundColor: "#4caf50",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                mt: 0.2,
                              }}
                            >
                              <Typography sx={{ color: "#fff", fontSize: 12, fontWeight: 600 }}>✓</Typography>
                            </Box>
                            <Box
                              component="span"
                              dangerouslySetInnerHTML={{ __html: prepareHtml(work.description) }}
                              sx={{
                                fontFamily,
                                fontSize: 13,
                                color: "#333",
                                lineHeight: 1.6,
                                wordBreak: 'break-word',
                                '& p': { margin: 0 },
                                '& div': { margin: 0 },
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })()}

                {/* Warranty Block */}
                {(() => {
                  let allWarranties = [];
                  
                  // New structure: proposal.warranties (contract level)
                  if (proposal.warranties && proposal.warranties.length > 0) {
                    allWarranties = proposal.warranties.map(w => ({ ...w }));
                  }
                  // Fallback: aggregate from proposalSystems (filtered by accepted option if applicable)
                  else if (proposal.proposalSystems) {
                    const filteredSystems = proposal.optionAccepted && proposal.selectedOptionId
                      ? proposal.proposalSystems.filter(s => s.optionId === proposal.selectedOptionId)
                      : proposal.proposalSystems;
                    filteredSystems.forEach((system) => {
                      if (system.warranties && system.warranties.length > 0) {
                        system.warranties.forEach((w) => {
                          allWarranties.push({ ...w, systemName: system.name });
                        });
                      }
                    });
                  }
                  
                  if (allWarranties.length === 0) return null;
                  
                  return (
                    <Box className="print-section pdf-section" sx={{ mb: 4, px: 2 }}>
                      {/* Header */}
                      <Box sx={{ display: 'inline-block', mb: 0 }}>
                        <Box
                          sx={{
                            backgroundColor: '#0d3c61',
                            color: '#fff',
                            px: 2,
                            py: 0.75,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily,
                              fontWeight: 600,
                              fontSize: 14,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Warranties
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, overflow: 'hidden' }}>
                        {allWarranties.map((warranty, idx) => (
                          <Box 
                            key={idx} 
                            sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              p: 1.5,
                              borderBottom: idx < allWarranties.length - 1 ? '1px solid #e0e0e0' : 'none',
                            }}
                          >
                            <Typography sx={{ fontFamily, fontSize: 13, color: '#333' }}>
                              {warranty.description}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 600, color: '#0d3c61' }}>
                              {isNaN(warranty.years) ? warranty.years : `${warranty.years} Years`}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })()}

            {/* PAGE 3: Investment, Guarantees, Signature */}
            {/* Investment Table */}
            <Box className="print-section pdf-section">
              <ProposalViewInvestment
              lineItems={getLineItems()}
              totalPay={totalPay}
              rebates={totalRebates}
              instants={totalInstants}
              discountsList={proposal?.discounts || []}
              rebatesList={proposal?.contractRebates || []}
              financingOptions={proposal.paymentNotes}
              isSigned={!!(proposal?.signatureId || proposal?.signature)}
              />
            </Box>

            {/* Maxx Guarantees */}
            <Box className="print-section pdf-section">
              <ProposalViewGuarantees guarantees={proposal?.guarantees} />
            </Box>

            {/* Signature Section */}
            <Box id="signature-section" className="print-section pdf-section">
              <ProposalViewSignature
                signature={proposal.signature}
                onSign={onAcceptProposal}
                token={!!token}
                isAccepted={!!proposal.signature}
                customerName={((proposal.selectedClientId && proposal.property?.clients?.find((c) => c.client?.id === proposal.selectedClientId)?.client) || proposal.property?.clients?.[0]?.client)?.fullName}
                isAuthUser={isAuthUser}
              />
            </Box>

            {/* Terms and Conditions link */}
            <Box sx={{ textAlign: 'center', mb: 3, ...no_print }}>
              <Typography
                component="a"
                href="https://airmaxx.com/wp-content/uploads/2022/06/Home-Improvement-Construction-Contract-_-Owner-and-Contractor.pdf"
                target="_blank"
                rel="noReferrer"
                sx={{
                  fontFamily,
                  fontSize: 12,
                  color: '#0d3c61',
                  textDecoration: 'underline',
                  '&:hover': {
                    color: '#0a2d4a',
                  },
                }}
              >
                View Full Terms and Conditions
              </Typography>
            </Box>

            {/* Additional Information for Customer */}
            {proposal.informationForCustomer?.length > 0 && (
              <Box className="additional-info-section no-print">
                <ProposalViewAdditionalInfo info={proposal.informationForCustomer} />
              </Box>
            )}
            {/* Footer */}
            <ProposalViewFooter 
              techPhone={proposal.createdBy?.phoneNumber}
              techEmail={proposal.createdBy?.email}
            />
          </Box>
        </Paper>

      </Box>

      {!token && <EmailProposalModal isOpen={emailModalOpen} onClose={handleEmailModalClose} data={proposal} />}

      {/* Staff-only: client view history */}
      {!token && (
        <Dialog open={viewsOpen} onClose={() => setViewsOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color="success" /> Client Views — {proposal.publicId}
          </DialogTitle>
          <DialogContent dividers>
            {viewsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : viewsData ? (
              <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip color="success" label={`${viewsData.uniqueDays || 0} unique day(s)`} />
                  <Chip variant="outlined" label={`${viewsData.totalOpens || 0} total open(s)`} />
                  {viewsData.lastViewedAt && (
                    <Chip variant="outlined" label={`Last: ${formatViewDate(viewsData.lastViewedAt)}`} />
                  )}
                  {viewsData.botCount > 0 && (
                    <Chip variant="outlined" color="warning" size="small" label={`${viewsData.botCount} bot/scanner hit(s) ignored`} />
                  )}
                </Box>

                {viewsData.summary && Object.keys(viewsData.summary).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">By channel:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {Object.entries(viewsData.summary).map(([source, n]) => (
                        <Chip key={source} size="small" label={`${source}: ${n}`} />
                      ))}
                    </Box>
                  </Box>
                )}

                <Divider sx={{ mb: 1 }} />
                <Typography variant="caption" color="text.secondary">Recent opens (latest 100):</Typography>
                <Box sx={{ mt: 1, maxHeight: 320, overflow: 'auto' }}>
                  {(viewsData.views || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No opens recorded.</Typography>
                  ) : (
                    (viewsData.views || []).map((v) => (
                      <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f0f0f0', opacity: v.isBot ? 0.5 : 1 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatViewDate(v.viewedAt)}
                            {v.isBot && <Chip size="small" color="warning" variant="outlined" label="bot" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                          </Typography>
                          {v.ip && <Typography variant="caption" color="text.secondary">{v.ip}</Typography>}
                        </Box>
                        <Chip size="small" label={v.source} />
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No data.</Typography>
            )}
          </DialogContent>
        </Dialog>
      )}

      <ToastContainer />
    </Fragment>
  );
};

export default ProposalView1;
